<style>
  .import-file--hidden {
    display: none;
  }
</style>

<template>
  <div id="pageImport">
    <v-container grid-list-xl fluid>
      <v-layout row wrap>
        <v-flex sm12 style="text-align:right">
          <h3 style="float:left">Импорт данных: {{ title }}</h3>
          <v-btn
                  color="blue"
                  dark
                  small
                  fab
                  @click="refresh()"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </v-flex>

        <!-- Загрузка файла -->
        <v-flex xs12>
          <v-card>
            <v-card-title>
              <span class="headline">Загрузка файла</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <p>Для импорта данных скачайте шаблон в формате <b>.xlsx</b> (он определяет обязательные поля), заполните его данными и загрузите файл.</p>

              <!-- Сообщения и ошибки -->
              <v-alert
                      v-if="alert"
                      :value="true"
                      :type="alert.type"
                      dismissible
                      class="mb-3"
                      @input="clearAlert()"
              >
                <div>{{ alert.message }}</div>
                <ul v-if="alert.errors && alert.errors.length" class="mb-0 mt-1">
                  <li v-for="(error, index) in alert.errors" :key="index">{{ error }}</li>
                </ul>
              </v-alert>

              <!-- Индикатор загрузки и обработки файла -->
              <v-layout v-if="isLoading || isValidating" row wrap align-center class="mb-3">
                <v-flex xs12>
                  <v-progress-circular
                          indeterminate
                          color="primary"
                          :size="24"
                          :width="2"
                  ></v-progress-circular>
                  <span class="ml-2">{{ isValidating ? 'Файл проверяется, пожалуйста, подождите...' : 'Файл загружается и обрабатывается, пожалуйста, подождите...' }}</span>
                </v-flex>
              </v-layout>

              <input
                      class="import-file--hidden"
                      type="file"
                      id="importFile"
                      ref="fileInput"
                      accept=".csv,.xls,.xlsx"
                      @change="onFileChange"
              />

              <v-layout row wrap align-center>
                <v-flex xs12>
                  <v-btn
                          color="primary"
                          :loading="isLoading"
                          @click="pickFile"
                  >
                    <v-icon left>attach_file</v-icon>
                    Выбрать файл
                  </v-btn>
                  <v-btn
                          color="success"
                          :disabled="!file"
                          :loading="isLoading"
                          @click="importData()"
                  >
                    <v-icon left>file_upload</v-icon>
                    Импортировать
                  </v-btn>
                  <v-btn
                          color="grey"
                          :loading="isLoading"
                          @click="downloadTemplate()"
                  >
                    <v-icon left>cloud_download</v-icon>
                    Скачать шаблон (.xlsx)
                  </v-btn>
                  <span class="ml-3">{{ fileName || 'Файл не выбран' }}</span>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Результат импорта -->
        <v-flex xs12>
          <v-card>
            <v-card-title>
              <span class="headline">Результат импорта</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <v-data-table
                      :headers="complex.headers"
                      :items="importResult"
                      expand
                      :select-all="false"
                      class="elevation-1"
                      item-key="Id"
                      no-data-text="Нет данных об импорте"
                      no-results-text="Нет результатов"
                      rows-per-page-text="На странице"
              >
                <template slot="items" slot-scope="props">
                  <td>{{ props.item.Timestamp }}</td>
                  <td>{{ props.item.FileName || '—' }}</td>
                  <td>{{ props.item.Status || '—' }}</td>
                  <td>{{ props.item.Total }}</td>
                  <td>{{ props.item.Accepted }}</td>
                  <td>{{ props.item.Aborted }}</td>
                  <td class="text-xs-center">
                    <v-btn
                            icon
                            small
                            @click="props.expanded = !props.expanded"
                    >
                      <v-icon>{{ props.expanded ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                  </td>
                </template>
                <template slot="expand" slot-scope="props">
                  <div class="px-3 py-3">
                    <div class="title mb-2">Детали импорта</div>
                    <v-layout row wrap align-center>
                      <v-flex xs12>
                        <v-chip small color="blue" text-color="white" class="mr-2">
                          Всего: {{ props.item.Total }}
                        </v-chip>
                        <v-chip small color="green" text-color="white" class="mr-2">
                          Принято: {{ props.item.Accepted }}
                        </v-chip>
                        <v-chip small color="red" text-color="white" class="mr-2">
                          Отклонено: {{ props.item.Aborted }}
                        </v-chip>
                      </v-flex>
                    </v-layout>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
    import DataImportService from '@/services/DataImportService';

    const FILE_EXTENSIONS = ['xlsx', 'xls', 'csv'];

    export default {
        name: 'ImportDataView',
        props: {
            type: {
                type: String,
                required: true,
            },
            title: {
                type: String,
                required: true,
            },
        },
        data () {
            return {
                file: null,
                fileName: '',
                alert: null,
                isValidating: false,
                complex: {
                    headers: [
                        {
                            text: 'Дата',
                            value: 'Timestamp'
                        },
                        {
                            text: 'Файл',
                            value: 'FileName'
                        },
                        {
                            text: 'Статус',
                            value: 'Status'
                        },
                        {
                            text: 'Всего',
                            value: 'Total'
                        },
                        {
                            text: 'Принято',
                            value: 'Accepted'
                        },
                        {
                            text: 'Отклонено',
                            value: 'Aborted'
                        },
                        {
                            text: '',
                            value: '__expand',
                            sortable: false,
                            align: 'center',
                            width: '48px'
                        }
                    ],
                },
                abortedHeaders: [
                    {
                        text: 'Сущность',
                        value: 'entityId'
                    },
                    {
                        text: 'Ошибка',
                        value: 'errorText'
                    },
                    {
                        text: 'Строка',
                        value: 'lineNumber'
                    },
                ]
            };
        },

        computed: {

            /**
             * История импортов для текущего типа
             * @returns {Array}
             */
            importResult () {
                return DataImportService.state.history[this.type] || [];
            },

            /**
             * Флаг загрузки
             * @returns {*}
             */
            isLoading () {
                return DataImportService.state.isLoading;
            },

            /**
             * Сводка последнего импорта: { total, accepted, aborted }
             * @returns {*}
             */
            importSummary () {
                const result = DataImportService.state.result;

                return (result && result.records) ? result.records : null;
            },

            /**
             * Отклоненные записи последнего импорта
             * @returns {Array}
             */
            abortedRecords () {
                return (this.importSummary && this.importSummary.aborted) || [];
            },

            /**
             * Файл с отклоненными строками (если есть)
             * @returns {*}
             */
            errorFile () {
                return DataImportService.state.errorFile;
            },
        },

        created () {
            this.refresh();
        },

        methods: {

            /**
             * Открыть диалог выбора файла
             */
            pickFile () {
                this.$refs.fileInput.click();
            },

            /**
             * Обработка выбранного файла
             * @param event
             */
            onFileChange (event) {
                const file = event.target.files[0];

                if (!file) {
                    return;
                }

                const ext = file.name.split('.').pop().toLowerCase();

                if (FILE_EXTENSIONS.indexOf(ext) === -1) {
                    this.setAlert('error', 'Недопустимый формат файла. Используйте шаблон .xlsx/.xls/.csv.');
                    event.target.value = '';
                    return;
                }

                this.file = file;
                this.fileName = file.name;
                this.clearAlert();
                DataImportService.clearResult();
            },

            /**
             * Запуск импорта выбранного файла
             * Перед загрузкой файл проходит валидацию схемы и типов данных.
             */
            importData () {
                if (!this.file) {
                    return;
                }

                this.clearAlert();
                this.isValidating = true;

                DataImportService.validateFile(this.type, this.file).then(result => {
                    this.isValidating = false;

                    if (!result.valid) {
                        this.setAlert('error', `Ошибки валидации файла (строк с данными: ${result.rowCount}):`, result.errors);
                        return;
                    }

                    DataImportService.importFile(this.type, this.file).then(() => {
                        const summary = this.importSummary;

                        if (summary) {
                            const aborted = (summary.aborted || []).length;

                            this.setAlert(
                                aborted ? 'warning' : 'success',
                                `Импорт завершен: всего ${summary.total}, принято ${summary.accepted}, отклонено ${aborted}.`
                            );
                        } else {
                            this.setAlert('success', 'Данные успешно импортированы.');
                        }

                        this.refresh();
                    }).catch(err => {
                        this.setAlert('error', this.getMessage(err) || 'Ошибка при импорте данных!');
                    });
                }).catch(err => {
                    this.isValidating = false;
                    this.setAlert('error', this.getMessage(err) || 'Ошибка при чтении файла!');
                });
            },

            /**
             * Получение истории импортов
             */
            refresh () {
                DataImportService.getImportHistory(this.type).catch(() => {});
            },

            /**
             * Скачивание шаблона для импорта
             */
            downloadTemplate () {
                this.clearAlert();

                DataImportService.downloadTemplate(this.type).then(() => {
                    this.setAlert('info', 'Шаблон успешно скачан.');
                }).catch(err => {
                    this.setAlert('error', this.getMessage(err) || 'Ошибка при скачивании шаблона!');
                });
            },

            /**
             * Установить сообщение в области алертов
             * @param type
             * @param message
             * @param errors - список ошибок валидации
             */
            setAlert (type, message, errors) {
                this.alert = {
                    type,
                    message,
                    errors: errors || [],
                };
            },

            /**
             * Сбросить сообщение в области алертов
             */
            clearAlert () {
                this.alert = null;
            },

            /**
             * Скачивание файла с отклоненными строками
             */
            downloadErrorFile () {
                if (!this.errorFile) {
                    return;
                }

                DataImportService.downloadStaticFile(this.errorFile.url, this.errorFile.filename);
            },

            /**
             * Извлечение текста ошибки из ответа сервера
             * @param err
             * @returns {*}
             */
            getMessage (err) {
                if (err && err.response && err.response.data) {
                    if (typeof err.response.data === 'string') {
                        return err.response.data;
                    }
                    if (err.response.data.message) {
                        return err.response.data.message;
                    }
                }

                if (err && err.message) {
                    return err.message;
                }

                return null;
            },
        }
    };
</script>


// WEBPACK FOOTER //
// src/components/import/ImportDataView.vue